import { Button } from "@/components/ui/button";
import { ArrowRight, Play, Globe, Award, Users, Clock } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import aiTechImage1 from "@/assets/hero-ai-tech-1.jpg";
import aiTechImage2 from "@/assets/hero-ai-tech-2.jpg";
import aiTechImage3 from "@/assets/hero-ai-tech-3.jpg";
import aiTechImage4 from "@/assets/hero-ai-tech-4.jpg";
import aiTechImage5 from "@/assets/hero-ai-tech-5.jpg";
import aiTechImage6 from "@/assets/hero-ai-tech-6.jpg";
import CMMILevel3 from "@/assets/cmmi-level-3-badge-BSa7Q4e9.png";
import ISO27001 from "@/assets/iso-27001-badge-CQ6tote3.png";
import ISO9001 from "@/assets/iso-9001-badge-C3ypBXVO.png";
import { CriticalImage, useImagePreload } from "@/components/ImageOptimization";
import { TechParticles, NetworkLines } from "@/components/TechAnimations";
import FloatingCertifications from "./FloatingCertifications";

const Hero = () => {
  const [isHeroVisible, setIsHeroVisible] = useState(true);
  const heroRef = useRef<HTMLElement | null>(null);
  const techImages = [
    aiTechImage1,
    aiTechImage2,
    aiTechImage3,
    aiTechImage4,
    aiTechImage5,
    aiTechImage6,
  ];
  // Only preload the first critical image
  useImagePreload([techImages[0]]);

  // Smooth background carousel
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);

  const aiTechnologies = [
    "TensorFlow",
    "PyTorch",
    "OpenAI GPT",
    "Hugging Face",
    "Scikit-learn",
    "Keras",
    "Apache Spark",
    "MLflow",
    "Weights & Biases",
    "Ray",
    "NVIDIA CUDA",
    "Transformers",
    "BERT",
    "LangChain",
    "Stable Diffusion",
  ];

  const aiDomains = [
    "Computer Vision",
    "Natural Language Processing",
    "Generative AI",
    "Reinforcement Learning",
    "Deep Learning",
    "Machine Learning",
    "Neural Networks",
    "Large Language Models",
    "AI Automation",
    "Predictive Analytics",
    "AI Chatbots",
    "AI Drug Discovery",
  ];

  // Ultra smooth carousel interval
  useEffect(() => {
    const carouselInterval = setInterval(() => {
      setCurrentCarouselIndex((prev) => (prev + 1) % techImages.length);
    }, 8000); // 8 second intervals
    return () => clearInterval(carouselInterval);
  }, [techImages.length]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsHeroVisible(entry.isIntersecting);
      },
      { threshold: 0.3 },
    );

    if (heroRef.current) {
      observer.observe(heroRef.current);
    }

    return () => {
      if (heroRef.current) {
        observer.unobserve(heroRef.current);
      }
    };
  }, []);

  // Static AI Tech Stack Display
  const AITechCarousel = () => (
    <div className="absolute top-5 left-5 backdrop-blur-sm bg-white/80 rounded-lg p-3 border border-primary/20 shadow-lg">
      <div className="text-xs text-muted-foreground mb-1">AI Technology</div>
      <div className="text-sm font-mono text-primary font-semibold">
        {aiTechnologies[0]}
      </div>
    </div>
  );

  // Static AI Domain Display
  const AIDomainDisplay = () => (
    <div className="absolute top-5 right-5 backdrop-blur-sm bg-white/80 rounded-lg p-3 border border-primary/20 shadow-lg">
      <div className="text-xs text-muted-foreground mb-1">AI Domain</div>
      <div className="text-sm font-semibold text-primary">{aiDomains[0]}</div>
    </div>
  );

  // Static Code Display
  const DynamicCodeAnimation = () => {
    const aiCodeSnippets = [
      "import tensorflow as tf",
      "from transformers import AutoModel",
      "import torch.nn as nn",
      "from sklearn import ML",
      "import openai.GPT",
      "from langchain import LLM",
    ];

    return (
      <div className="absolute bottom-5 right-5 font-mono text-xs text-primary bg-white/80 rounded p-2 border border-primary/20 backdrop-blur-sm shadow-lg">
        <div>{aiCodeSnippets[0]}</div>
      </div>
    );
  };

  // Static AI Model Architecture
  const AIModelArchitecture = () => {
    const aiModels = [
      { name: "GPT", type: "Transformer", nodes: 8 },
      { name: "BERT", type: "Encoder", nodes: 6 },
      { name: "ResNet", type: "CNN", nodes: 4 },
      { name: "LSTM", type: "RNN", nodes: 5 },
      { name: "GAN", type: "Generator", nodes: 7 },
    ];

    const currentModel = aiModels[0];

    return (
      <div className="absolute top-1/4 left-10 backdrop-blur-sm bg-white/80 rounded-lg p-4 border border-primary/20 shadow-lg">
        <div className="text-xs text-muted-foreground mb-2">AI Model</div>
        <div className="text-lg font-bold text-primary mb-1">
          {currentModel.name}
        </div>
        <div className="text-xs text-foreground/70 mb-2">
          {currentModel.type}
        </div>
        <div className="flex gap-1">
          {Array.from({ length: currentModel.nodes }).map((_, i) => (
            <div
              key={i}
              className={`w-2 h-2 ${i % 2 === 0 ? "bg-primary shadow-glow" : "bg-secondary shadow-glow-secondary"} rounded-full`}
            />
          ))}
        </div>
      </div>
    );
  };

  // AI Neural Network Animation with secondary color accents
  const NeuralNetwork = () => (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <svg className="w-full h-full opacity-20" viewBox="0 0 800 600">
        <defs>
          <radialGradient id="neuralGrad" cx="50%" cy="50%" r="50%">
            <stop
              offset="0%"
              stopColor="hsl(var(--primary))"
              stopOpacity="0.8"
            />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
          <radialGradient id="neuralGradSecondary" cx="50%" cy="50%" r="50%">
            <stop
              offset="0%"
              stopColor="hsl(var(--secondary))"
              stopOpacity="0.6"
            />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
        </defs>
        {Array.from({ length: 15 }).map((_, i) => (
          <circle
            key={i}
            cx={50 + (i % 5) * 150}
            cy={100 + Math.floor(i / 5) * 120}
            r="4"
            fill={
              i % 2 === 0 ? "url(#neuralGrad)" : "url(#neuralGradSecondary)"
            }
            className="animate-network-pulse"
            style={{ animationDelay: `${i * 0.15}s` }}
          />
        ))}
        {Array.from({ length: 12 }).map((_, i) => (
          <line
            key={i}
            x1={50 + (i % 5) * 150}
            y1={100 + Math.floor(i / 5) * 120}
            x2={50 + ((i + 1) % 5) * 150}
            y2={100 + Math.floor((i + 1) / 5) * 120}
            stroke={
              i % 2 === 0 ? "hsl(var(--primary))" : "hsl(var(--secondary))"
            }
            strokeWidth="1"
            opacity={i % 2 === 0 ? "0.3" : "0.2"}
            className="animate-data-flow"
            style={{ animationDelay: `${i * 0.25}s` }}
          />
        ))}
      </svg>
    </div>
  );

  // Perfect Crossfade Background Carousel
  const SmoothBackgroundCarousel = () => (
    <div className="absolute inset-0 overflow-hidden">
      {techImages.map((image, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-[3000ms] ease-in-out ${
            index === currentCarouselIndex ? "opacity-100" : "opacity-0"
          }`}
          style={{
            transitionTimingFunction: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
            willChange: "opacity",
          }}
        >
          <CriticalImage
            src={image}
            alt={`AI Technology background ${index + 1}: Advanced artificial intelligence and machine learning solutions`}
            width={1920}
            height={1080}
            className="w-full h-full object-cover object-center"
            quality={95}
            sizes="100vw"
            priority={index === 0}
            lazy={index !== 0}
            fetchpriority={index === 0 ? "high" : "low"}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/45 to-black/50"></div>
        </div>
      ))}
    </div>
  );

  // Floating AI Terms
  const FloatingAITerms = () => {
    const aiTerms = [
      "Machine Learning",
      "Neural Networks",
      "Deep Learning",
      "AI Models",
      "Algorithms",
      "Data Science",
      "Computer Vision",
      "NLP",
      "TensorFlow",
      "PyTorch",
      "GPT",
      "Transformers",
    ];

    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {aiTerms.map((term, i) => (
          <div
            key={i}
            className={`absolute ${i % 2 === 0 ? "text-primary/10" : "text-secondary/10"} text-xs font-mono animate-[aiFloat_15s_ease-in-out_infinite]`}
            style={{
              left: `${Math.random() * 80 + 10}%`,
              top: `${Math.random() * 80 + 10}%`,
              animationDelay: `${i * 1.2}s`,
              animationDirection: i % 2 === 0 ? "normal" : "reverse",
            }}
          >
            {term}
          </div>
        ))}
      </div>
    );
  };

  return (
    <section
      ref={heroRef}
      id="main-content"
      className="relative min-h-screen flex items-center justify-center overflow-hidden py-20"
      role="banner"
      aria-label="Smartborn AI-powered IT services hero section"
      tabIndex={-1}
    >
      {/* Smooth Background Carousel */}
      <div className="absolute inset-0 z-0">
        <SmoothBackgroundCarousel />
      </div>

      {/* AI Animated Background Elements */}
      <NeuralNetwork />
      <FloatingCertifications />

      {/* Content */}
      <div className="relative z-20 container mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <TechParticles count={30} className="opacity-50" />
        <NetworkLines className="opacity-40" />

        <div className="max-w-5xl mx-auto">
          <div className="backdrop-blur-md bg-white/95 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 mb-4 sm:mb-6 border border-white/40 shadow-2xl">
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-foreground leading-tight animate-slide-up-fade font-bold tracking-tight">
              <span className="sr-only">Smartborn IT Services - </span>
              <span className="block mb-2 text-foreground">
                Enterprise Software & Cloud Solutions
              </span>
              <span
                className="block bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent animate-scale-fade-in leading-tight font-extrabold tracking-tight"
                aria-label="SAP, Salesforce, Workday & Custom Development"
              >
                SAP, Salesforce, Workday & Custom Development
              </span>
            </h1>
          </div>

          <div className="backdrop-blur-md bg-white/90 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-5 max-w-3xl mx-auto mb-6 sm:mb-8 border border-white/30 shadow-lg">
            <p className="text-xs sm:text-sm md:text-base lg:text-lg text-foreground leading-relaxed">
              Implementing enterprise applications, building custom software,
              and providing dedicated engineering teams for businesses across
              manufacturing, healthcare, and retail sectors.
            </p>
          </div>

          {/* Stats with AI Theme */}
          {/* <section
            aria-label="Smartborn company achievements and statistics in AI and technology"
            className="animate-slide-up-fade"
            style={{ animationDelay: "1.2s" }}
          >
            <div
              className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5 max-w-6xl mx-auto px-2 sm:px-4"
              role="region"
              aria-label="Company statistics"
            >
              <div
                className="text-center group backdrop-blur-md bg-gradient-to-br from-white/95 to-white/90 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-5 border-2 border-primary/30 hover:border-primary transition-all duration-500 shadow-card hover:shadow-elegant hover:-translate-y-1 animate-fade-in"
                role="article"
                aria-labelledby="stat-1"
              >
                <Globe
                  className="h-5 w-5 sm:h-6 sm:w-6 text-primary mx-auto mb-2 group-hover:scale-110 transition-transform duration-300"
                  aria-hidden="true"
                />
                <div
                  id="stat-1"
                  className="text-xl sm:text-2xl md:text-3xl font-bold text-primary mb-1"
                  aria-label="One hundred eighty plus projects delivered"
                >
                  180+
                </div>
                <div className="text-foreground font-semibold text-xs sm:text-sm mb-1">
                  Projects
                </div>
                <div className="text-muted-foreground text-[10px] sm:text-xs">
                  Delivered Worldwide
                </div>
              </div>
              <div
                className="text-center group backdrop-blur-md bg-gradient-to-br from-white/95 to-white/90 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-5 border-2 border-primary/30 hover:border-primary transition-all duration-500 shadow-card hover:shadow-elegant hover:-translate-y-1 animate-fade-in"
                style={{ animationDelay: "0.1s" }}
                role="article"
                aria-labelledby="stat-2"
              >
                <Award
                  className="h-5 w-5 sm:h-6 sm:w-6 text-primary mx-auto mb-2 group-hover:scale-110 transition-transform duration-300"
                  aria-hidden="true"
                />
                <div
                  id="stat-2"
                  className="text-xl sm:text-2xl md:text-3xl font-bold text-primary mb-1"
                  aria-label="Twenty five plus domains expertise"
                >
                  25+
                </div>
                <div className="text-foreground font-semibold text-xs sm:text-sm mb-1">
                  Industries
                </div>
                <div className="text-muted-foreground text-[10px] sm:text-xs">
                  Global Coverage
                </div>
              </div>
              <div
                className="text-center group backdrop-blur-md bg-gradient-to-br from-white/95 to-white/90 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-5 border-2 border-primary/30 hover:border-primary transition-all duration-500 shadow-card hover:shadow-elegant hover:-translate-y-1 animate-fade-in"
                style={{ animationDelay: "0.2s" }}
                role="article"
                aria-labelledby="stat-3"
              >
                <Users
                  className="h-5 w-5 sm:h-6 sm:w-6 text-primary mx-auto mb-2 group-hover:scale-110 transition-transform duration-300"
                  aria-hidden="true"
                />
                <div
                  id="stat-3"
                  className="text-xl sm:text-2xl md:text-3xl font-bold text-primary mb-1"
                  aria-label="Two hundred plus employees"
                >
                  200+
                </div>
                <div className="text-foreground font-semibold text-xs sm:text-sm mb-1">
                  Employees
                </div>
                <div className="text-muted-foreground text-[10px] sm:text-xs">
                  Expert Team
                </div>
              </div>
              <div
                className="text-center group backdrop-blur-md bg-gradient-to-br from-white/95 to-white/90 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-5 border-2 border-primary/30 hover:border-primary transition-all duration-500 shadow-card hover:shadow-elegant hover:-translate-y-1 animate-fade-in"
                style={{ animationDelay: "0.3s" }}
                role="article"
                aria-labelledby="stat-4"
              >
                <Clock
                  className="h-5 w-5 sm:h-6 sm:w-6 text-primary mx-auto mb-2 group-hover:scale-110 transition-transform duration-300"
                  aria-hidden="true"
                />
                <div
                  id="stat-4"
                  className="text-xl sm:text-2xl md:text-3xl font-bold text-primary mb-1"
                  aria-label="Eight plus years experience"
                >
                  8+
                </div>
                <div className="text-foreground font-semibold text-xs sm:text-sm mb-1">
                  Years
                </div>
                <div className="text-muted-foreground text-[10px] sm:text-xs">
                  Industry Experience
                </div>
              </div>
            </div>
          </section> */}
        </div>
      </div>

      {/* AI-themed Scroll Indicator */}
      <div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-sync-bounce"
        aria-label="Scroll down indicator"
      >
        <div className="w-6 h-10 border-2 border-primary/50 rounded-full flex justify-center backdrop-blur-sm bg-white/80">
          <div
            className="w-1 h-3 bg-primary rounded-full mt-2 animate-data-flow shadow-glow"
            aria-hidden="true"
          ></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
